import { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, ExternalLink, Search, Phone } from "lucide-react";
import CategoryNav from "./CategoryNav";
import { useManagedCollection, slugify } from "../admin/useManagedCollection";
import { AdminItem, AdminAddBar } from "../admin/AdminControls";
import type { FieldDef } from "../admin/AdminFieldModal";
import NewsTicker from './NewsTicker';
import SocialLinks, { SOCIAL_FIELDS } from './SocialLinks';
import FilterChip from './FilterChip';

// Every temple/spiritual-service listing's public detail-page URL is derived
// automatically from its name: /temples/<name-slug>. Same pattern as
// tailoringSlug in Tailoring.tsx — no manual per-listing page creation needed.
export function templeSlug(name: string) {
  return (name || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

const TYPE_OPTIONS = ["Temple", "Spiritual Services"];

const TEMPLE_FIELDS: FieldDef[] = [
  { key: "name", label: "Temple name", required: true },
  { key: "type", label: "Type", type: "select", options: TYPE_OPTIONS },
  { key: "city", label: "City", placeholder: "Frisco" },
  { key: "address", label: "Address", type: "textarea" },
  { key: "website", label: "Website URL", type: "url", placeholder: "https://…" },
  { key: "image", label: "Image URL", type: "image", placeholder: "https://…" },
  ...SOCIAL_FIELDS,
];

export interface Temple {
  name: string;
  city: string;
  address: string;
  website: string;
  image: string;
  type?: string;
  phone?: string;
  // Short tagline shown under the name on the grid card.
  shortDescription?: string;
  // Detail-page-only copy (shown on /temples/<slug>, not on the card).
  detailDescription?: string;
  // Social URL rendered only on the detail page (hidden from the grid card
  // when hideCardActions is set).
  instagram?: string;
  // Skip the social-links row on the grid card (links stay on detail page).
  hideCardActions?: boolean;
  // Force this listing to sort last on the Temples page.
  pinLast?: boolean;
  // Tie-breaker among pinLast listings: higher = closer to the end.
  pinOrder?: number;
}

const LOCATION_FILTERS = ['Addison', 'Allen', 'Anna', 'Arlington', 'Aubrey', 'Carrollton', 'Cedar Hill', 'Coppell', 'Dallas', 'Denton', 'DeSoto', 'Duncanville', 'Farmers Branch', 'Flower Mound', 'Fort Worth', 'Frisco', 'Garland', 'Grand Prairie', 'Hutchins', 'Irving', 'Lewisville', 'Mansfield', 'McKinney', 'Mesquite', 'Midlothian', 'Plano', 'Richardson', 'Rockwall', 'Rowlett', 'Terrell', 'Other'];

const HERO_LOCATIONS = ['All Locations', 'Addison', 'Allen', 'Anna', 'Arlington', 'Aubrey', 'Carrollton', 'Cedar Hill', 'Coppell', 'Dallas', 'Denton', 'DeSoto', 'Duncanville', 'Farmers Branch', 'Flower Mound', 'Fort Worth', 'Frisco', 'Garland', 'Grand Prairie', 'Hutchins', 'Irving', 'Lewisville', 'Mansfield', 'McKinney', 'Mesquite', 'Midlothian', 'Plano', 'Prosper', 'Richardson', 'Rockwall', 'Rowlett', 'Southlake', 'Terrell', 'The Colony', 'Other'];

// Photos go in /public/temples/<file>. Until they're added, the onError fallback
// shows a temple stock image so cards never look broken.
export const TEMPLES: Temple[] = [
  { name: "Karya Siddhi Hanuman Temple", city: "Frisco", address: "12030 Independence Pkwy, Frisco, TX 75035", website: "https://dallashanuman.org/", image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1780827533891-ag4986.png" },
  { name: "Radha Krishna Temple of Dallas", city: "Allen", address: "1450 N Watters Rd, Allen, TX 75013", website: "https://www.radhakrishnatemple.net/", image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1780827591787-hzdvl.png" },
  { name: "DFW Hindu Temple", city: "Irving", address: "1605 N Britain Rd, Irving, TX 75061", website: "https://www.dfwhindutemple.org/", image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1780827674001-p17bmkc.png" },
  { name: "Sri Guruvayurappan Temple Dallas", city: "Carrollton", address: "1701 Spring Valley Rd, Carrollton, TX 75006", website: "https://info.guruvayurappan.us/", image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1780827763266-tmsacp.png" },
  { name: "Radha Kalachandji Temple", city: "Dallas", address: "5430 Gurley Ave, Dallas, TX 75223", website: "https://www.iskcondallas.org/", image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1780827802357-chm6t.png" },
  { name: "Hindu Temple of Greater Fort Worth", city: "Fort Worth", address: "3000 Longvue Ave, Fort Worth, TX 76108", website: "https://htgfw.org/", image: "https://storage.googleapis.com/happyspace-generated-images/generated/2026-06-15/e44b0e53f49f.jpeg" },
  { name: "BAPS Shri Swaminarayan Mandir", city: "Irving", address: "4601 N State Highway 161, Irving, TX 75038", phone: "(972) 243-8669", website: "https://www.baps.org/Global-Network/North-America/Dallas.aspx", image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1781570025955-e3eit.png" },
  { name: "North Texas Hindu Mandir", city: "Dallas", address: "10309 Baronne Cir, Dallas, TX 75218", website: "https://www.nthm.org/", image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1781570146921-cvhdnb.png" },
  { name: "Shri Ganesha Temple", city: "Plano", address: "6508 K Ave, Plano, TX 75074", website: "https://www.shriganeshatemple.org/", image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1781570593228-ggga6h.png" },
  { name: "Shirdi Sai Baba Temple of DFW", city: "Irving", address: "6441 W Northgate Dr, Irving, TX 75063", website: "https://www.shirdisaidallas.org/", image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1781571033331-bk2c6.png" },
  { name: "Siddhayatan Tirth", city: "Windom", address: "120 State Hwy 121, Windom, TX 75492", website: "https://siddhayatan.org/", image: "" },
  { name: "Sri Krishna Vrundavana Dallas", city: "Plano", address: "4345 Waskom Dr, Plano, TX 75024", website: "", image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1783704912914-3md3o6.png" },
  { name: "Sri Venkateswara Swamy Temple", city: "Frisco", address: "2552 Stonebrook Pkwy #600, Frisco, TX 75034", website: "", image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1783705363909-z6qwmr.png" },
  { name: "Dallas Texas Temple", city: "Dallas", address: "6363 Willow Ln, Dallas, TX 75230", phone: "(972) 991-1273", website: "", image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1783706182152-l6dnzm.png" },
  { name: "Shree Raghunath Temple", city: "Plano", address: "8901 Independence Pkwy Ste 100, Plano, TX 75023", website: "", image: "" },
  { name: "Shree Swaminarayan Gurukul Rajkot Sansthan", city: "Plano", address: "621 Park Vista Rd, Plano, TX 75094", website: "", image: "" },
  { name: "Kapardi Shirdi Sai Temple", city: "Plano", address: "6205 Coit Rd STE 201, Plano, TX 75024", website: "", image: "" },
  { name: "Shri Ram Mandir Plano", city: "Plano", address: "6521 Chase Oaks Blvd, Plano, TX 75023", website: "", image: "" },
  {
    name: "Astrologer and Spiritual Healer Psychic Services",
    city: "McKinney",
    address: "",
    website: "",
    image: "https://storage.googleapis.com/happyspace-generated-images/form-uploads/2026-10-23/c105651e92f6.jpeg",
    type: "Spiritual Services",
    phone: "3173898050",
    instagram: "https://www.instagram.com/psychic_vikram/",
    detailDescription:
      "Welcome to INDIAN ASTROLOGER AND PSYCHIC, represented by Vikram Ji, offering Indian astrology consultations, psychic readings, personalized spiritual guidance, and traditional Hindu spiritual services in the McKinney and Plano, Texas area. Our services include astrology consultations, spiritual readings, guidance for personal questions, traditional Hindu poojas and ceremonies, and spiritual cleansing and protection rituals. Each consultation is approached with respect for Indian spiritual traditions and an emphasis on personal attention, privacy, and a welcoming experience. Whether you are exploring Indian astrology, seeking spiritual insight, or interested in traditional Hindu rituals, Vikram Ji welcomes inquiries about available services and consultations.",
    hideCardActions: true,
    pinLast: true,
    pinOrder: 1
  },
  {
    name: "Astrologer Kumar",
    city: "Frisco",
    address: "",
    website: "https://www.astrokumar.com",
    image: "https://storage.googleapis.com/happyspace-generated-images/form-uploads/2026-07-22/9d4cc0ca2340.jpeg",
    type: "Spiritual Services",
    phone: "945-212-6111",
    shortDescription: "Astrologer and Panditji in Frisco, TX",
    hideCardActions: true,
    pinLast: true,
    pinOrder: 2
  }
];

const FALLBACK_IMAGE = "https://images.unsplash.com/photo-1604608672516-f1b9b1d37076?w=600&h=400&fit=crop";

// Top: the important, lead-with-it paragraph shown right under the H1.
const TEMPLE_INTRO_TOP =
  "Find Hindu temples and Indian places of worship across Dallas\u2013Fort Worth. Explore temples in Dallas, Frisco, Plano, Irving, Carrollton, Allen, McKinney, and surrounding DFW communities. Find temple addresses, directions, prayer services, puja, darshan, aarti, festivals, and community events.";

// Bottom: supporting detail (cities served + festivals/traditions), shown after all the cards.
const TEMPLE_INTRO_BOTTOM =
  "Explore Hindu temples and Indian places of worship throughout DFW, including Dallas, Frisco, Plano, Irving, McKinney, Allen, Carrollton, Richardson, Lewisville, Coppell, and surrounding communities. Browse temple locations, learn about different traditions and communities, and find information about festivals, puja, aarti, darshan, and community gatherings.";

function mapsUrl(t: Temple) {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${t.name}, ${t.address}`)}`;
}

export function TempleCard({ temple, index }: { temple: Temple; index: number }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.4, delay: index * 0.08 }}
      className="bg-white rounded-lg border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden flex flex-col"
      style={{ fontFamily: "'Poppins', sans-serif" }}
    >
      <Link to={`/temples/${templeSlug(temple.name)}`} className="flex flex-col flex-1">
        <div className="w-full h-[80px] relative overflow-hidden">
          <img
            src={temple.image}
            alt={temple.name}
            className="w-full h-full object-cover"
            loading="lazy"
            onError={(e) => { (e.target as HTMLImageElement).src = FALLBACK_IMAGE; }}
          />
        </div>
        <div className="p-2 pb-1 flex-1">
          <h3 className="text-[12px] font-bold text-gray-900 leading-tight mb-0.5 line-clamp-2">
            {(() => {
              const commaIndex = temple.name.indexOf(",");
              if (commaIndex === -1) return temple.name;
              return (
                <>
                  {temple.name.slice(0, commaIndex)}
                  <span className="font-normal">{temple.name.slice(commaIndex)}</span>
                </>
              );
            })()}
          </h3>
          {temple.shortDescription && (
            <p className="text-[10px] text-gray-500 leading-snug mb-0.5">{temple.shortDescription}</p>
          )}
          {temple.address && (
            <div className="flex items-start gap-1">
              <MapPin className="w-2.5 h-2.5 text-orange-500 shrink-0 mt-0.5" />
              <span className="text-[10px] text-gray-500 font-medium leading-snug line-clamp-2">{temple.address}</span>
            </div>
          )}
        </div>
      </Link>
      <div className="px-2 pb-2 pt-1 space-y-1">
        {!temple.hideCardActions && (
          <SocialLinks item={{ ...temple, website: "", websiteUrl: "", websiteLink: "", website_url: "" }} px={20} />
        )}
        {temple.phone && (
          <a href={`tel:${temple.phone.replace(/[^0-9+]/g, "")}`} className="flex items-center justify-center gap-1 bg-[#16a34a] hover:bg-[#15803d] text-white text-[10px] font-semibold py-1 rounded-md transition-colors duration-200">
            <Phone className="w-2.5 h-2.5" /> Call Now
          </a>
        )}
        {/^\d/.test(temple.address.trim()) && (
          <a href={mapsUrl(temple)} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-1 bg-[#f97316] hover:bg-[#ea580c] text-white text-[10px] font-semibold py-1 rounded-md transition-colors duration-200">
            <ExternalLink className="w-2.5 h-2.5" /> View Location
          </a>
        )}
      </div>
    </motion.div>
  );
}

const TYPE_FILTERS = ["All Types", ...TYPE_OPTIONS];

export default function Temples() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedHeroLocation, setSelectedHeroLocation] = useState("All Locations");
  const [selectedType, setSelectedType] = useState("All Types");
  const [showDropdown, setShowDropdown] = useState(false);
  const [dropdownSearch, setDropdownSearch] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const dropdownInputRef = useRef<HTMLInputElement>(null);

  const mc = useManagedCollection<any>("temples", TEMPLES, (t) => slugify(t.name));

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
        setDropdownSearch("");
      }
    }
    if (showDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
      setTimeout(() => dropdownInputRef.current?.focus(), 50);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [showDropdown]);

  const filteredHeroLocations = HERO_LOCATIONS.filter((loc) =>
    loc.toLowerCase().includes(dropdownSearch.toLowerCase())
  );

  const filtered = mc.items
    .filter((t) => {
      const matchesQuery = searchQuery.trim() === "" || t.name.toLowerCase().includes(searchQuery.trim().toLowerCase());
      const matchesLocation = selectedHeroLocation === "All Locations" || t.city.toLowerCase() === selectedHeroLocation.toLowerCase();
      const matchesType = selectedType === "All Types" || (t.type || "Temple") === selectedType;
      return matchesQuery && matchesLocation && matchesType;
    })
    // Always alphabetical by name, regardless of insertion order — this keeps
    // cards sorted whether they came from the static TEMPLES seed list, a
    // builder edit, or a new admin-added listing (mc.items merges all sources).
    // Listings flagged pinLast (e.g. the Astrologer psychic-services card) are
    // held at the end of the grid instead of their alphabetical position.
    .sort((a, b) => {
      const pa = a.pinLast ? 1 : 0;
      const pb = b.pinLast ? 1 : 0;
      if (pa !== pb) return pa - pb;
      // Among pinned listings, higher pinOrder sits closer to the end.
      if (pa && pb) return (a.pinOrder ?? 0) - (b.pinOrder ?? 0);
      return a.name.localeCompare(b.name, undefined, { sensitivity: "base" });
    });

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section className="relative w-full" style={{ minHeight: "160px" }}>
        <div className="absolute inset-0 overflow-hidden">
          <img
            src="https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1780931863237-827yjl.png"
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/45" />
          <svg viewBox="0 0 1440 24" preserveAspectRatio="none" style={{ position: "absolute", left: 0, bottom: "-1px", width: "100%", height: "18px", display: "block" }}>
            <path d="M0,0 Q720,24 1440,0 L1440,24 L0,24 Z" fill="#ffffff" />
          </svg>
        </div>
        <div className="relative flex flex-col items-center justify-center" style={{ zIndex: 10, minHeight: "160px" }}>
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }} className="w-full max-w-[740px]">
            <div className="rounded-md" style={{ boxShadow: "0 0 40px 12px rgba(255,255,255,0.45), 0 0 80px 24px rgba(255,255,255,0.2)", backdropFilter: "blur(4px)" }}>
              <div className="flex flex-col sm:flex-row bg-white rounded-md shadow-md h-auto sm:h-[40px]">
                <div className="flex-1 px-3 py-2 sm:py-0 flex items-center border-b sm:border-b-0 sm:border-r border-gray-200 min-h-[40px]">
                  <span className="text-[11px] text-gray-600 font-semibold shrink-0 mr-2 hidden sm:inline">Find a temple</span>
                  <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search temples by name..." className="w-full text-[12px] text-gray-700 placeholder:text-gray-400 outline-none bg-transparent" />
                </div>
                <div ref={dropdownRef} className="relative border-b sm:border-b-0 sm:border-r border-gray-200 min-h-[40px] sm:w-[170px] md:w-[190px] shrink-0" style={{ zIndex: 99999 }}>
                  <button onClick={() => setShowDropdown(!showDropdown)} className="w-full h-full px-3 py-2 sm:py-0 flex items-center gap-1.5 text-left min-h-[40px]">
                    <MapPin className="w-3.5 h-3.5 text-gray-500 shrink-0" />
                    <span className="text-[12px] text-gray-700 font-medium truncate flex-1">{selectedHeroLocation}</span>
                    <svg className={"w-3 h-3 text-gray-400 shrink-0 transition-transform " + (showDropdown ? "rotate-180" : "")} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {showDropdown && (
                    <div
                      className="absolute top-full left-0 bg-white border border-gray-100 rounded-lg overflow-hidden"
                      style={{ zIndex: 99999, marginTop: "4px", width: "200px", boxShadow: "0 10px 24px rgba(15,23,42,0.16)", fontFamily: "'Poppins', sans-serif" }}
                    >
                      <div className="px-3 py-2 border-b border-gray-100">
                        <input
                          ref={dropdownInputRef}
                          type="text"
                          value={dropdownSearch}
                          onChange={(e) => setDropdownSearch(e.target.value)}
                          placeholder="Search locations..."
                          className="w-full text-[12px] text-gray-700 placeholder:text-gray-400 outline-none bg-transparent"
                        />
                      </div>
                      <div className="overflow-y-auto" style={{ maxHeight: "200px" }}>
                        {filteredHeroLocations.length === 0 ? (
                          <div className="px-3 py-4 text-center text-[12px] text-gray-400">No locations found</div>
                        ) : (
                          filteredHeroLocations.map((loc) => (
                            <button
                              key={loc}
                              onClick={() => { setSelectedHeroLocation(loc); setShowDropdown(false); setDropdownSearch(""); }}
                              className="w-full text-left flex items-center transition-colors duration-150"
                              style={{
                                padding: "8px 12px",
                                fontSize: "13px",
                                fontWeight: selectedHeroLocation === loc ? 700 : 500,
                                color: selectedHeroLocation === loc ? "#f97316" : "#111827",
                                background: selectedHeroLocation === loc ? "#fff3e8" : "transparent",
                                height: "36px",
                              }}
                              onMouseEnter={e => { if (selectedHeroLocation !== loc) (e.currentTarget as HTMLButtonElement).style.background = "#fff7ed"; }}
                              onMouseLeave={e => { if (selectedHeroLocation !== loc) (e.currentTarget as HTMLButtonElement).style.background = "transparent"; }}
                            >
                              {loc}
                            </button>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>
                <button onClick={() => setShowDropdown(false)} className="bg-[#f97316] hover:bg-[#ea580c] text-white text-[13px] font-bold px-5 py-2.5 sm:py-0 transition-colors duration-200 shrink-0 min-h-[40px] sm:rounded-r-md w-full sm:w-auto">
                  <span className="flex items-center justify-center gap-1.5">
                    <Search className="w-3.5 h-3.5 sm:hidden" />
                    Search
                  </span>
                </button>
              </div>
            </div>

          </motion.div>
        </div>
      </section>
      <NewsTicker />

      <CategoryNav />

      <section className="bg-gray-50/40 py-8" style={{ fontFamily: "'Poppins', sans-serif" }}>
        <div className="max-w-[1320px] w-[calc(100%-40px)] mx-auto px-4 flex flex-col lg:flex-row gap-6">
          {/* LEFT: temples */}
          <div className="flex-1 min-w-0">
            <h1 className="text-[16px] sm:text-[18px] font-bold text-gray-900 mb-3" style={{ fontFamily: "'Lora', serif" }}>
              Hindu Temples in Dallas & DFW
            </h1>

            {/* Top: the important, lead paragraph */}
            <p className="text-[12px] sm:text-[13px] text-gray-500 mb-4">
              {TEMPLE_INTRO_TOP}
            </p>

            <div className="flex flex-wrap gap-2 mb-5">
              {TYPE_FILTERS.map((type) => (
                <FilterChip key={type} active={selectedType === type} onClick={() => setSelectedType(type)} color="#f97316">
                  {type}
                </FilterChip>
              ))}
            </div>
            <AdminAddBar mc={mc} fields={TEMPLE_FIELDS} label="Add Temple" />
            {filtered.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
                {filtered.map((t, i) => (
                  <AdminItem key={t._key} mc={mc} item={t} fields={TEMPLE_FIELDS} title="Temple">
                    <TempleCard temple={t as any} index={i} />
                  </AdminItem>
                ))}
              </div>
            ) : (
              <div className="bg-white border border-dashed border-gray-200 rounded-2xl p-8 text-center text-[13px] text-gray-500">
                No temples match your filters.
              </div>
            )}

            {/* Bottom: supporting detail, shown after all the cards */}
            <div className="mt-8 pt-6 border-t border-gray-200">
              <p className="text-[12px] sm:text-[13px] text-gray-500">
                {TEMPLE_INTRO_BOTTOM}
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
